import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

const NotificationContext = createContext(null);
const isBrowser = typeof window !== 'undefined';
const DEFAULT_DURATION = 3000;
let incrementalId = 0;

const createNotification = ({ message, type = 'info', duration = DEFAULT_DURATION }) => ({
  id: `notification-${Date.now()}-${incrementalId++}`,
  message,
  type,
  duration,
  createdAt: Date.now(),
});

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const timersRef = useRef(new Map());

  const removeNotification = useCallback((id) => {
    setNotifications((prev) => prev.filter((notification) => notification.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const scheduleRemoval = useCallback((notification) => {
    if (!notification.duration) return;
    const timer = window.setTimeout(() => removeNotification(notification.id), notification.duration);
    timersRef.current.set(notification.id, timer);
  }, [removeNotification]);

  const notify = useCallback(
    ({ message, type = 'info', duration = DEFAULT_DURATION }) => {
      if (!message) return null;
      const notification = createNotification({ message, type, duration });
      setNotifications((previous) => [...previous, notification]);
      if (isBrowser) {
        scheduleRemoval(notification);
      }
      return notification.id;
    },
    [scheduleRemoval],
  );

  useEffect(() => () => {
    timersRef.current.forEach((timeoutId) => clearTimeout(timeoutId));
    timersRef.current.clear();
  }, []);

  useEffect(() => {
    if (!isBrowser) return undefined;

    const handleGlobalNotification = (event) => {
      const detail = event.detail || {};
      notify({
        message: detail.message,
        type: detail.type,
        duration: detail.duration,
      });
    };

    const globalNotify = (message, type = 'info', duration = DEFAULT_DURATION) =>
      notify({ message, type, duration });

    window.addEventListener('notify', handleGlobalNotification);
    window.showNotification = globalNotify;

    return () => {
      window.removeEventListener('notify', handleGlobalNotification);
      if (window.showNotification === globalNotify) {
        delete window.showNotification;
      }
    };
  }, [notify]);

  const contextValue = useMemo(
    () => ({
      notify,
      removeNotification,
    }),
    [notify, removeNotification],
  );

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
      <div className="notification-stack" aria-live="polite" aria-atomic="true">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={[
              'notification',
              notification.type === 'success' ? 'notification--success' : '',
              notification.type === 'error' ? 'notification--error' : '',
              notification.type === 'warning' ? 'notification--warning' : '',
              notification.type === 'info' ? 'notification--info' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            role="alert"
          >
            <i className={`fas fa-${notification.type === 'success' ? 'check-circle' : notification.type === 'error' ? 'exclamation-triangle' : 'info-circle'}`} />
            <span>{notification.message}</span>
            <button
              type="button"
              className="notification-close-btn"
              onClick={() => removeNotification(notification.id)}
              aria-label="Cerrar notificación"
            >
              <i className="fas fa-times" />
            </button>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

